import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormGroup, FormGroupDirective } from '@angular/forms';
import { CompanyAddressFormModel } from '@registration/interfaces';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, of, switchMap } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { filter } from 'rxjs/operators';
import { CountryEnum } from '../../../../shared/enums/country-enum';

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-address-search-autocomplete',
  templateUrl: './address-search-autocomplete.component.html',
  styleUrls: ['./address-search-autocomplete.component.scss'],
})
export class AddressSearchAutocompleteComponent
  implements OnInit, AfterViewInit
{
  @Input({ required: true })
  public title: string;
  @Input({ required: false })
  public withClearIcon?: boolean = false;
  @Input({ required: false })
  public tabOrder?: number;
  @Input({ required: false })
  public isCo2: boolean = false;

  public foundedPlaces$ = new BehaviorSubject(null);

  public addressForm: FormGroup<CompanyAddressFormModel>;
  public errorKey: string = null;
  public countryEnum = CountryEnum;

  private Place = null;

  public constructor(
    private readonly parentForm: FormGroupDirective,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  public async ngOnInit(): Promise<void> {
    if (this.parentForm?.control) {
      this.addressForm = this.parentForm.control?.get(
        'companyAddress'
      ) as FormGroup;
      if (this.addressForm?.get('address')?.value != '') {
        this.checkAddress();
      }
    }
    this.changeDetectorRef.detectChanges();

    const { Place } = (await google?.maps?.importLibrary(
      'places'
    )) as google.maps.PlacesLibrary;
    this.Place = Place;
  }

  public ngAfterViewInit() {
    this.addressForm
      .get('address')
      .valueChanges.pipe(
        untilDestroyed(this),
        filter(value => value?.length > 5),
        switchMap(value => {
          if (value) {
            if (!this.Place) return of([]);
            return this.Place?.searchByText({
              fields: ['formattedAddress', 'addressComponents', 'location'],
              textQuery: value,
            });
          }
          return of([]);
        })
      )
      .subscribe(({ places }) => {
        if (!!places) {
          this.checkAddress();
        }
        this.foundedPlaces$.next(places);
      });
  }

  public async onKeydownEnter() {
    const places = this.foundedPlaces$.getValue();
    if (places.length > 0) {
      this.setAddressForm(places[0]);
    } else {
      this.checkAddress();
    }
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent) {
    const place = event.option.value as google.maps.places.Place;
    this.setAddressForm(place);
  }

  private setAddressForm(place: google.maps.places.Place) {
    const addressComponent = place.addressComponents;
    this.addressForm.setValue(
      {
        address: place.formattedAddress,
        street: this.getAddressComponentByType(addressComponent, 'route'),
        streetNumber: this.getAddressComponentByType(
          addressComponent,
          'street_number'
        ),
        city: this.getAddressComponentByType(addressComponent, 'locality') !== ''
              ? this.getAddressComponentByType(addressComponent, 'locality')
              : this.getAddressComponentByType(addressComponent, 'political'),
        zipCode: this.getAddressComponentByType(
          addressComponent,
          'postal_code'
        ),
        lat: place.location.lat(),
        lng: place.location.lng(),
        countryId: Number(this.countryEnum[this.getAddressComponentByType(
          addressComponent,
          'country',
          'shortText'
        )]),
      },
      { onlySelf: true }
    );
    this.checkAddress();
  }

  private checkAddress() {
    if (this.addressForm.valid) {
      this.addressForm.get('address')?.setErrors(null);
      this.errorKey = null;
    } else {
      Object.keys(this.addressForm.controls).forEach(key => {
        const control = this.addressForm.get(key);
        if (control.invalid) {
          this.errorKey = key;
          this.addressForm.get('address')?.setErrors({ required: true });
        }
      });
    }
  }

  private getAddressComponentByType(
    addressComponent: google.maps.places.AddressComponent[],
    type: string,
    textForm: string = "longText"
  ) {
    const types = addressComponent.filter(x => x?.types.includes(type));
    if (types.length > 0) {
      return types[0]?.[textForm];
    } else {
      return '';
    }
  }
}
